import { FC, ReactElement, useState, useMemo } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { IoMdAdd } from "react-icons/io";
import { Search as SearchIcon } from "@mui/icons-material";

import { columns } from "@/columns/COMMON/product-certificates";
import {
  useGetProductCertsQuery,
  useDeleteProductCertMutation,
} from "@/services";
import {
  useDebounce,
  useSearchQuery,
  useSnackbar,
} from "@/hooks";
import { useAppSelector } from "@/store";
import { DocumentAccessLevel, roles } from "@/roles";
import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import Pagination from "@/components/paginations/pagination";

interface CertificateManagementProps {
  onUploadClick: () => void;
  onViewCertificate: (certificateId: string) => void;
  onEditCertificate: (certificateId: string) => void;
}

const CertificateManagement: FC<CertificateManagementProps> = ({
  onUploadClick,
  onViewCertificate,
  onEditCertificate,
}): ReactElement => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [actionLoading, setActionLoading] = useState<{
    view: string[];
    download: string[];
    delete: string[];
    edit: string[];
  }>({
    view: [],
    download: [],
    delete: [],
    edit: [],
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateSummary | null>(null);

  const { show } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const { updateQuery } = useSearchQuery();

  const [deleteProductCert] = useDeleteProductCertMutation();

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false;
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, isTDAdmin]
  );

  const clientId = useMemo(() => {
    if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdmin, selectedTenant, selectedClient, user]);

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetProductCertsQuery(
    {
      clientId: clientId!,
      pageNo: page,
      pageSize: limit,
    },
    {
      skip: shouldSkip || !clientId,
      refetchOnMountOrArgChange: true,
    }
  );

  const canUploadCerts = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  const handleViewCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({ ...prev, view: [...prev.view, cert._id] }));
    try {
      onViewCertificate(cert._id);
    } finally {
      setActionLoading(prev => ({ ...prev, view: prev.view.filter(id => id !== cert._id) }));
    }
  };

  const handleDownloadCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({ ...prev, download: [...prev.download, cert._id] }));
    try {
      show("Download Certificate", "Please open the certificate details to download the CUSMA/USMCA/T-MEC certificate", "info");
      onViewCertificate(cert._id);
    } catch (error) {
      show("Download failed", "Failed to download the certificate file", "error");
    } finally {
      setActionLoading(prev => ({ ...prev, download: prev.download.filter(id => id !== cert._id) }));
    }
  };

  const handleDeleteCertificate = (cert: CertificateSummary) => {
    setCertificateToDelete(cert);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCertificate = async () => {
    if (!certificateToDelete) return;
    setActionLoading(prev => ({ ...prev, delete: [...prev.delete, certificateToDelete._id] }));
    try {
      const result = await deleteProductCert(certificateToDelete._id).unwrap();
      if (result) {
        show("Certificate deleted", `Certificate "${certificateToDelete.fileName}" was successfully deleted`, "success");
      }
    } catch (error) {
      show("Delete failed", "Failed to delete the certificate. Please try again.", "error");
    } finally {
      setActionLoading(prev => ({ ...prev, delete: prev.delete.filter(id => id !== certificateToDelete._id) }));
      setDeleteDialogOpen(false);
      setCertificateToDelete(null);
    }
  };

  const handleEditCertificate = async (cert: CertificateSummary) => {
    setActionLoading(prev => ({ ...prev, edit: [...prev.edit, cert._id] }));
    try {
      onEditCertificate(cert._id);
    } finally {
      setActionLoading(prev => ({ ...prev, edit: prev.edit.filter(id => id !== cert._id) }));
    }
  };

  const handleActions = (type: "view" | "download" | "delete" | "edit", cert: CertificateSummary) => {
    switch (type) {
      case "view": handleViewCertificate(cert); break;
      case "download": handleDownloadCertificate(cert); break;
      case "delete": handleDeleteCertificate(cert); break;
      case "edit": handleEditCertificate(cert); break;
    }
  };

  const filterCertificates = useDebounce<string>((search) => {
    updateQuery({ search });
    setPage(1);
  }, 1200);

  const { data = [], meta } = apiData ?? {};
  const countCerts = meta?.countDocs ?? 0;

  const cols = columns({ action: handleActions, actionsLoading: actionLoading });

  if (shouldSkip || !clientId) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h6" sx={{ color: tokens.text.secondary }}>
          Please select a client to view certificates
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: "100%", minHeight: 0, flex: 1, flexDirection: "column" }}>
      {/* Header + Filters */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" gap={1.5}>
          <Typography color={tokens.text.primary} fontSize={24} fontWeight={tokens.fontWeight.semibold}>
            Certificate Management{" "}
            <Typography component="span" color={tokens.text.muted} fontSize={24} fontWeight={tokens.fontWeight.normal}>
              ({countCerts})
            </Typography>
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <TextField
              size="small"
              placeholder="Search certificates..."
              onChange={(e) => filterCertificates(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': { borderRadius: tokens.radius.md, height: 36 },
                '& .MuiInputBase-input': { fontSize: tokens.fontSize.sm2 },
              }}
            />
            {canUploadCerts && (
              <Button
                variant="primary-contained"
                startIcon={<IoMdAdd />}
                sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, boxShadow: 'none' }}
                onClick={onUploadClick}
              >
                Upload Certificate
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Table section */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Stack sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          mx: 3,
          mb: 2,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          overflow: "hidden",
          ...tableCompactSx,
        }}>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: tokens.radius.lg }} />
          ) : (
            <>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                {isFetching && !isLoading && (
                  <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%", minWidth: { xs: 700, md: "100%" } }}>
                  <TableHead>
                    <TableRow>
                      {cols.map((col) => (
                        <TableCell
                          key={col.key as string}
                          align={(col.align as any) || "left"}
                          sx={{ width: col.width, minWidth: col.minWidth }}
                        >
                          {col.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length === 0 && !isFetching ? (
                      <TableRow>
                        <TableCell colSpan={cols.length} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            No certificates found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((cert: any) => (
                        <TableRow key={cert._id}>
                          {cols.map((col) => (
                            <TableCell
                              key={`${cert._id}-${col.key}`}
                              align={(col.align as any) || "left"}
                            >
                              {col.component?.(cert)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination — inside container */}
              {countCerts > 0 && (
                <Pagination
                  page={page}
                  limit={limit}
                  total={countCerts}
                  onPageChange={setPage}
                  onLimitChange={(l) => { setLimit(l); setPage(1); }}
                />
              )}
            </>
          )}
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Certificate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the certificate "{certificateToDelete?.fileName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading.delete.includes(certificateToDelete?._id || "")}
            sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteCertificate}
            color="error"
            variant="contained"
            disabled={actionLoading.delete.includes(certificateToDelete?._id || "")}
            sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CertificateManagement;
